import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../components/Layout';
import { getTeacherStats, getTeacherSubjects, getLiveClasses } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { BookOpen, Video, Users, DollarSign, Calendar, ChevronRight } from 'lucide-react';

export default function TeacherDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, subjectsData] = await Promise.all([
          getTeacherStats(), getTeacherSubjects()
        ]);
        setStats(statsData);
        setSubjects(subjectsData);
        // Fetch classes for each subject
        const allClasses = [];
        for (const sub of subjectsData) {
          const cls = await getLiveClasses(null, sub.id);
          allClasses.push(...cls);
        }
        const now = new Date();
        setUpcomingClasses(
          allClasses.filter(c => new Date(c.scheduled_at) > now)
            .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)).slice(0, 5)
        );
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <Layout><div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="teacher-dashboard">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A]">
            Hola, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-[#737373] mt-2">Panel de Profesor</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-[#737373]">Mis Cursos</p><p className="text-3xl font-bold text-[#1A1A1A]">{stats?.total_courses || 0}</p></div>
                <div className="h-12 w-12 rounded-full bg-[#8B1538]/10 flex items-center justify-center"><BookOpen className="h-6 w-6 text-[#8B1538]" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-[#737373]">Estudiantes</p><p className="text-3xl font-bold text-[#1A1A1A]">{stats?.total_students || 0}</p></div>
                <div className="h-12 w-12 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center"><Users className="h-6 w-6 text-[#2D6A4F]" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-[#737373]">Clases</p><p className="text-3xl font-bold text-[#1A1A1A]">{stats?.total_classes || 0}</p></div>
                <div className="h-12 w-12 rounded-full bg-[#D9B48F]/30 flex items-center justify-center"><Video className="h-6 w-6 text-[#B8860B]" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-[#737373]">Ingresos</p><p className="text-3xl font-bold text-[#1A1A1A]">${(stats?.total_revenue || 0).toLocaleString()}</p></div>
                <div className="h-12 w-12 rounded-full bg-[#8B1538]/10 flex items-center justify-center"><DollarSign className="h-6 w-6 text-[#8B1538]" /></div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xl text-[#1A1A1A]">Mis Cursos</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/teacher/courses')} className="text-[#8B1538] hover:text-[#70102B]">Ver todos <ChevronRight className="h-4 w-4 ml-1" /></Button>
            </CardHeader>
            <CardContent>
              {subjects.length > 0 ? (
                <div className="space-y-4">
                  {subjects.slice(0, 3).map((sub) => (
                    <div key={sub.id} data-testid={`teacher-subject-${sub.id}`} className="flex items-center justify-between p-4 bg-[#F5F2EA] rounded-md">
                      <div>
                        <h4 className="font-medium text-[#1A1A1A]">{sub.title}</h4>
                        <p className="text-xs text-[#737373]">{sub.module_title} — {sub.classes_count} clases</p>
                      </div>
                      <div className="h-7 w-7 rounded-full bg-[#8B1538] text-white flex items-center justify-center text-xs font-bold">{sub.order}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="h-12 w-12 mx-auto text-[#D9B48F] mb-3" />
                  <p className="text-[#737373]">No tienes cursos asignados</p>
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xl text-[#1A1A1A]">Próximas Clases</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/teacher/classes')} className="text-[#8B1538] hover:text-[#70102B]">Ver todas <ChevronRight className="h-4 w-4 ml-1" /></Button>
            </CardHeader>
            <CardContent>
              {upcomingClasses.length > 0 ? (
                <div className="space-y-4">
                  {upcomingClasses.map((cls) => (
                    <div key={cls.id} data-testid={`upcoming-class-${cls.id}`} className="flex items-center justify-between p-4 bg-[#F5F2EA] rounded-md">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-[#8B1538]/10 flex items-center justify-center"><Video className="h-5 w-5 text-[#8B1538]" /></div>
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{cls.title}</p>
                          <p className="text-sm text-[#737373]">{new Date(cls.scheduled_at).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })} - {new Date(cls.scheduled_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => navigate(`/teacher/class/${cls.id}`)} className="bg-[#8B1538] hover:bg-[#70102B] text-white">Iniciar</Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8"><Calendar className="h-12 w-12 mx-auto text-[#D9B48F] mb-3" /><p className="text-[#737373]">No hay clases programadas</p></div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
